import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';

import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { SignalR } from 'configurations/signalR';
import { ISurveyMetadata } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { useSessionStore } from 'session/useSessionStore';
import { useGetQueueGroupById, useGetUploadInformation } from '../api/useQueueGroupController';
import { useSetupReprocessing } from '../api/useQueueItemController';
import { useGetSeismicMetadata } from '../api/useSeismicMetadataController';
import { IQueueGroupItem, IQueueItemParam, SetupReprocessingItemParams } from '../models/interfaces/IQueueGroup';
import { QueueItemStatus } from '../models/enums/QueueItemStatus';
import { QueueType } from '../models/enums/QueueType';
import { QueueProgressData } from '../models/types/QueueProgressData';
import { SeismicMetadataPopup } from 'features/seismic/components/metadata/SeismicMetadataPopup';
import { getQueueTypeHandler } from '../queueType/QueueTypeRegistry';
import { QueueProcessingAddFilesWizard } from './QueueProcessingAddFilesWizard';
import { QueueProcessingAssociateAttributePopup } from './QueueProcessingAssociateAttributePopup';
import { QueueProcessingTraceHeaderPopup } from './QueueProcessingTraceHeaderPopup';
import { QueueProcessingCreateGroupPopup } from './QueueProcessingCreateGroupPopup';
import { getCopiedParamValues, setCopiedParamValues } from '../utils/copiedParamValues';
import { actionButtonSx, QUEUE_TYPE_META, STATUS_DISPLAY_ORDER, STATUS_META, StatusMeta } from '../utils/queueMeta';
import { QueueProcessingParams } from './QueueProcessingParams';

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    '& .detail-loading': {
        display: 'flex',
        justifyContent: 'center',
        padding: 64,
    },
    '& .detail-back-row': {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 20,
    },
    '& .detail-header-card': {
        flexShrink: 0,
        borderRadius: 12,
        border: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
        padding: 24,
        marginBottom: 24,
    },
    '& .detail-header-top': {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 16,
    },
    '& .detail-header-title-block': {
        flex: 1,
        minWidth: 200,
    },
    '& .detail-header-meta': {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 20,
        marginTop: 10,
    },
    '& .detail-meta-item': {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    '& .status-badge': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 4,
        paddingBottom: 4,
        borderRadius: 12,
        border: '1px solid transparent',
    },
    '& .detail-items-section': {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        marginTop: 8,
    },
    '& .detail-items-title': {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
    },
    '& .detail-items-list': {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        overflowY: 'auto',
        paddingRight: 4,
    },
    '& .item-card': {
        flexShrink: 0,
        borderRadius: 10,
        border: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
    },
    '& .item-card-header': {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        cursor: 'pointer',
    },
    '& .item-card-title-block': {
        flex: 1,
        minWidth: 0,
    },
    '& .item-card-body': {
        borderTop: '1px solid #f5f5f5',
        backgroundColor: '#fafafa',
        padding: '14px 16px',
    },
    '& .item-card-actions': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 12,
    },
    '& .item-card-dates': {
        display: 'flex',
        gap: 20,
        flexWrap: 'wrap',
        marginTop: 12,
    },
});

function validateReprocessParams(params: Record<string, unknown>): boolean {
    const p = params as Record<string, string>;
    const requiredFields = ['Srid', 'CoordinateX', 'CoordinateY', 'Inline', 'XLine', 'Trace', 'Cdp', 'ShotPoint', 'Domain'];
    const missing = requiredFields.filter((key) => !p[key]);

    if (missing.length > 0) {
        return false;
    }

    const hasByteScalar = !!p.ByteScalar;
    const hasXYScalar = !!p.ScalarX && !!p.ScalarY;

    if (!hasByteScalar && !hasXYScalar) {
        return false;
    }

    return true;
}

function isValidDate(dateStr: string | null | undefined): boolean {
    if (!dateStr) {
        return false;
    }
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) && isFinite(date.getTime());
}

function formatDate(dateStr: string | null): string {
    if (!dateStr || !isValidDate(dateStr)) {
        return '—';
    }
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatShortDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
}

interface IQueueTypeBadgeProps {
    queueType: QueueType;
    size?: 'small' | 'medium';
}

function QueueTypeBadge({ queueType, size = 'medium' }: IQueueTypeBadgeProps) {
    const meta = QUEUE_TYPE_META[queueType] ?? QUEUE_TYPE_META[QueueType.Unknown];

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: size === 'medium' ? 2 : 1.5,
                py: size === 'medium' ? 0.75 : 0.5,
                borderRadius: '14px',
                backgroundColor: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.color}33`,
            }}
        >
            <Typography
                variant={size === 'medium' ? 'body2' : 'caption'}
                sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1 }}
            >
                {meta.label}
            </Typography>
        </Box>
    );
}

type ResolvedItemDisplay = {
    status: QueueItemStatus;
    currentStep: number;
    totalSteps: number;
    percentage: number;
    stepDescription: string | null;
};

function resolveItemDisplay(item: IQueueGroupItem, liveProgress: QueueProgressData | undefined): ResolvedItemDisplay {
    const status = liveProgress?.status ?? item.Status;

    if (status === QueueItemStatus.Waiting) {
        return {
            status,
            currentStep: 0,
            totalSteps: 0,
            percentage: 0,
            stepDescription: null,
        };
    }

    if (!liveProgress || liveProgress.updateOnlyStatus) {
        const totalSteps = item.TotalSteps;
        const currentStep = item.CurrentStep;
        return {
            status,
            currentStep,
            totalSteps,
            percentage: totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : item.ProgressPercentage,
            stepDescription: item.StepDescription,
        };
    }

    const { step, totalSteps, percentage, log } = liveProgress;
    return {
        status,
        currentStep: step,
        totalSteps,
        percentage: totalSteps > 0 ? Math.round((step / totalSteps) * 100) : percentage,
        stepDescription: log,
    };
}

interface IParamsPopupProps {
    open: boolean;
    onChangeOpen: (open: boolean) => void;
    params: IQueueItemParam[];
    itemName: string;
}

function QueueProcessingParamsPopup({ open, onChangeOpen, params, itemName }: IParamsPopupProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const IGNORED_PARAMS = ['FilePath', 'FilesPath', 'DisplayName'];
        setCopiedParamValues(Object.fromEntries(params.filter((p) => !IGNORED_PARAMS.includes(p.Param)).map((p) => [p.Param, p.Value])));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <GeopostPopup
            open={open}
            onChangeOpen={onChangeOpen}
            title='Parameters'
            width={720}
            height={500}
            resizable={false}
            draggable
            overlay
            padding='20px'
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant='body1' sx={{ fontWeight: 600, color: '#212121' }}>
                        {itemName}
                    </Typography>
                    <Button
                        size='small'
                        variant='outlined'
                        startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                        onClick={handleCopy}
                        sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            borderColor: copied ? '#66bb6a' : '#c5cae9',
                            color: copied ? '#2e7d32' : '#3949ab',
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy values'}
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {params.map((param, index) => (
                        <Box
                            key={param.Param}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '10px 12px',
                                backgroundColor: index % 2 === 0 ? '#f5f7fa' : 'transparent',
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant='body2' sx={{ fontWeight: 600, color: '#424242', width: '25%', flexShrink: 0 }}>
                                {param.Param}
                            </Typography>
                            <Typography variant='body2' sx={{ color: '#616161', width: '75%', wordBreak: 'break-word' }}>
                                {param.Value}
                            </Typography>
                        </Box>
                    ))}
                    {params.length === 0 && (
                        <Typography variant='body2' sx={{ color: '#9e9e9e', textAlign: 'center', padding: 2 }}>
                            No parameters.
                        </Typography>
                    )}
                </Box>
            </Box>
        </GeopostPopup>
    );
}

interface IItemCardProps {
    item: IQueueGroupItem;
    liveProgress?: QueueProgressData;
    projectToken?: string;
    queueType: QueueType;
    groupToken: string;
    showCheckbox?: boolean;
    isSelected?: boolean;
    onToggleSelect?: (token: string) => void;
}

function ItemCard({ item, liveProgress, projectToken, queueType, groupToken, showCheckbox, isSelected, onToggleSelect }: IItemCardProps) {
    const [expanded, setExpanded] = useState(false);
    const [paramsOpen, setParamsOpen] = useState(false);
    const [metadataOpen, setMetadataOpen] = useState(false);
    const [metadataResult, setMetadataResult] = useState<ISurveyMetadata[]>([]);
    const [associateOpen, setAssociateOpen] = useState(false);
    const [traceHeaderOpen, setTraceHeaderOpen] = useState(false);
    const [reprocessOpen, setReprocessOpen] = useState(false);
    const [reprocessParams, setReprocessParams] = useState<Record<string, unknown>>({});
    const [reprocessInitialValues, setReprocessInitialValues] = useState<Record<string, string>>({});
    const [reprocessKey, setReprocessKey] = useState(0);
    const { status, currentStep, totalSteps, percentage: displayPercentage, stepDescription } = resolveItemDisplay(item, liveProgress);
    const statusMeta = STATUS_META[status];
    const hasProgress = totalSteps > 0;
    const hasEntity = !!item.EntityKey;
    const queryClient = useQueryClient();
    const { mutate: fetchMetadata, isLoading: isLoadingMetadata } = useGetSeismicMetadata();
    const { mutate: setupReprocessing, isLoading: isReprocessing } = useSetupReprocessing();

    const handleShowMetadata = () => {
        fetchMetadata({ volumeToken: item.EntityKey }, {
            onSuccess: (metadata) => {
                setMetadataResult([metadata]);
                setMetadataOpen(true);
            },
        });
    };

    const [validationError, setValidationError] = useState(false);

    const handleConfirmReprocess = () => {
        if (!validateReprocessParams(reprocessParams)) {
            setValidationError(true);
            return;
        }

        setValidationError(false);

        const paramsList: IQueueItemParam[] = Object.entries(reprocessParams).map(([key, value]) => ({
            Param: key,
            Value: String(value),
        }));

        setupReprocessing({
            Items: [{ Token: item.Token, Params: paramsList }],
        }, {
            onSuccess: () => {
                setReprocessOpen(false);
                setReprocessParams({});
                queryClient.invalidateQueries(['QueueGroupById', groupToken]);
            },
        });
    };

    return (
        <Fragment>
            <Paper elevation={0} className='item-card'>
                <Box className='item-card-header' onClick={() => setExpanded((prev) => !prev)}>
                    {showCheckbox && (
                        <Checkbox
                            checked={!!isSelected}
                            size='small'
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => onToggleSelect?.(item.Token)}
                            sx={{ p: 0, mr: 0, flexShrink: 0 }}
                        />
                    )}
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: statusMeta.color,
                            flexShrink: 0,
                            mt: '2px',
                        }}
                    />
                    <Box className='item-card-title-block'>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {item.Name || item.Token}
                        </Typography>
                        {stepDescription && (
                            <Typography
                                variant='caption'
                                sx={{ color: '#757575', display: 'block', mt: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {stepDescription}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                        {item.IsReprocessing && (
                            <Box
                                className='status-badge'
                                sx={{
                                    backgroundColor: '#fff3e0',
                                    borderColor: '#ffa726',
                                    color: '#e65100',
                                }}
                            >
                                <Typography variant='caption' sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1 }}>
                                    Reprocessing
                                </Typography>
                            </Box>
                        )}
                        <Box
                            className='status-badge'
                            sx={{
                                backgroundColor: statusMeta.bg,
                                borderColor: statusMeta.borderColor,
                                color: statusMeta.color,
                            }}
                        >
                            <Typography variant='caption' sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1 }}>
                                {statusMeta.label}
                            </Typography>
                        </Box>
                        {item.Params.length > 0 && (
                            <Tooltip title='View parameters' placement='top'>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    startIcon={<FormatListBulletedIcon sx={{ fontSize: 16 }} />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setParamsOpen(true);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: 12,
                                        padding: '2px 10px',
                                        minWidth: 0,
                                        borderColor: '#c5cae9',
                                        color: '#3949ab',
                                        '&:hover': {
                                            borderColor: '#3949ab',
                                            backgroundColor: '#f0f4ff',
                                        },
                                    }}
                                >
                                    Params
                                </Button>
                            </Tooltip>
                        )}
                        <Box sx={{ width: 120 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '3px' }}>
                                <Typography variant='caption' sx={{ color: '#9e9e9e', lineHeight: 1 }}>
                                    {hasProgress ? `Step ${currentStep}/${totalSteps}` : 'Progress'}
                                </Typography>
                                <Typography variant='caption' sx={{ color: statusMeta.color, fontWeight: 700, lineHeight: 1 }}>
                                    {displayPercentage}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant='determinate'
                                value={displayPercentage}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: `${statusMeta.progressColor}30`,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: statusMeta.progressColor,
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                        <IconButton size='small' sx={{ color: '#9e9e9e' }}>
                            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Box>
                </Box>
                <Collapse in={expanded}>
                    <Box className='item-card-body'>
                        {status !== QueueItemStatus.Waiting && (
                            <Box className='item-card-actions'>
                                {hasEntity && getQueueTypeHandler(queueType).renderItemActions({
                                    entityKey: item.EntityKey,
                                    queueToken: item.Token,
                                    isExpanded: expanded,
                                    projectToken,
                                    onAssociateAttributes: () => setAssociateOpen(true),
                                    onShowMetadata: handleShowMetadata,
                                    isLoadingMetadata,
                                    onTraceHeaderInspect: () => setTraceHeaderOpen(true),
                                })}
                                <Button
                                    size='small'
                                    variant='outlined'
                                    startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                                    sx={actionButtonSx}
                                    onClick={() => {
                                        const iv = Object.fromEntries(item.Params.map((p) => [p.Param, p.Value]));
                                        setReprocessParams(iv);
                                        setReprocessInitialValues(iv);
                                        setReprocessKey((k) => k + 1);
                                        setReprocessOpen(true);
                                    }}
                                >
                                    Reindex
                                </Button>
                            </Box>
                        )}
                        {status === QueueItemStatus.Success && !hasEntity && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                padding: '10px 12px',
                                borderRadius: 1,
                                backgroundColor: '#ffebee',
                                border: '1px solid #ef9a9a',
                                marginBottom: 1.5,
                            }}>
                                <ErrorOutlineIcon sx={{ fontSize: 16, color: '#c62828', mt: '1px', flexShrink: 0 }} />
                                <Typography variant='caption' sx={{ color: '#c62828', fontWeight: 600, wordBreak: 'break-word' }}>
                                    An error occurred during processing. No entity was generated.
                                </Typography>
                            </Box>
                        )}
                        {status === QueueItemStatus.Error && item.ErrorMessage && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                padding: '10px 12px',
                                borderRadius: 1,
                                backgroundColor: '#ffebee',
                                border: '1px solid #ef9a9a',
                                marginBottom: 1.5,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <ErrorOutlineIcon sx={{ fontSize: 16, color: '#c62828', mt: '1px', flexShrink: 0 }} />
                                    <Typography variant='caption' sx={{ color: '#c62828', fontWeight: 600, wordBreak: 'break-word' }}>
                                        {item.ErrorMessage}
                                    </Typography>
                                </Box>
                                {item.ErrorStackTrace && (
                                    <Typography
                                        variant='caption'
                                        component='pre'
                                        sx={{
                                            color: '#b71c1c',
                                            fontFamily: 'monospace',
                                            fontSize: 11,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            margin: 0,
                                            maxHeight: 160,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {item.ErrorStackTrace}
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <Box className='item-card-dates'>
                            <Box className='detail-meta-item'>
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                                <Typography variant='caption' sx={{ color: '#757575' }}>
                                    Created: {formatDate(item.CreationDate)}
                                </Typography>
                            </Box>
                            {isValidDate(item.LastUpdateDate) && (
                                <Box className='detail-meta-item'>
                                    <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                                    <Typography variant='caption' sx={{ color: '#757575' }}>
                                        Last update: {formatDate(item.LastUpdateDate)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            </Paper>
            <QueueProcessingParamsPopup
                open={paramsOpen}
                onChangeOpen={setParamsOpen}
                params={item.Params}
                itemName={item.Name || item.Token}
            />
            <QueueProcessingAssociateAttributePopup
                open={associateOpen}
                onChangeOpen={setAssociateOpen}
                volumeToken={item.EntityKey}
            />
            <QueueProcessingTraceHeaderPopup
                open={traceHeaderOpen}
                onChangeOpen={setTraceHeaderOpen}
                filePath={item.Params.find(p => p.Param === 'FilePath')?.Value ?? ''}
                itemParams={item.Params}
            />
            <SeismicMetadataPopup
                opened={metadataOpen}
                setOpened={setMetadataOpen}
                volumesMetadata={metadataResult}
            />
            <GeopostPopup
                open={reprocessOpen}
                onChangeOpen={setReprocessOpen}
                title='Reindex'
                width={600}
                height={600}
                resizable={false}
                draggable
                overlay
                footer={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        {validationError && (
                            <Typography variant='caption' sx={{ color: '#c62828', mr: 'auto' }}>
                                Preencha todos os campos obrigatórios.
                            </Typography>
                        )}
                        <Button
                            size='small'
                            variant='outlined'
                            startIcon={<ContentPasteIcon sx={{ fontSize: 14 }} />}
                            disabled={!getCopiedParamValues()}
                            onClick={() => {
                                const copied = getCopiedParamValues();
                                if (copied) {
                                    setReprocessParams(copied);
                                    setReprocessInitialValues(copied);
                                    setReprocessKey((k) => k + 1);
                                }
                            }}
                            sx={{ textTransform: 'none', mr: 'auto' }}
                        >
                            Paste values
                        </Button>
                        <Button variant='outlined' onClick={() => setReprocessOpen(false)} sx={{ textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button variant='contained' onClick={handleConfirmReprocess} disabled={isReprocessing} sx={{ textTransform: 'none' }}>
                            {isReprocessing ? 'Processando...' : 'Confirmar'}
                        </Button>
                    </Box>
                }
            >
                <QueueProcessingParams
                    key={reprocessKey}
                    queueType={queueType}
                    onParamsChange={setReprocessParams}
                    initialValues={reprocessInitialValues}
                />
            </GeopostPopup>
        </Fragment>
    );
}

interface IQueueProcessingGroupDetailProps {
    groupToken: string;
    onBack: () => void;
}

export function QueueProcessingGroupDetail({ groupToken, onBack }: IQueueProcessingGroupDetailProps) {
    const [isAddFilesOpen, setIsAddFilesOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [liveProgress, setLiveProgress] = useState<Record<string, QueueProgressData>>({});
    const [searchText, setSearchText] = useState('');
    const [activeStatuses, setActiveStatuses] = useState<Set<QueueItemStatus>>(new Set(STATUS_DISPLAY_ORDER));
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedTokens, setSelectedTokens] = useState<Set<string>>(new Set());
    const [batchReprocessOpen, setBatchReprocessOpen] = useState(false);
    const [batchReprocessParams, setBatchReprocessParams] = useState<Record<string, unknown>>({});
    const [batchReprocessInitialValues, setBatchReprocessInitialValues] = useState<Record<string, string>>({});
    const [batchReprocessKey, setBatchReprocessKey] = useState(0);
    const [batchValidationError, setBatchValidationError] = useState(false);
    const itemRefsMap = useRef<Record<string, HTMLDivElement | null>>({});

    const setItemRef = useCallback((token: string) => (el: HTMLDivElement | null) => {
        itemRefsMap.current[token] = el;
    }, []);
    const { data, isLoading } = useGetQueueGroupById(groupToken);
    const { data: uploadInfo } = useGetUploadInformation();
    const queryClient = useQueryClient();
    const { mutate: batchSetupReprocessing, isLoading: isBatchReprocessing } = useSetupReprocessing();

    const { tenantConfiguration, jwtToken } = useSessionStore(state => ({
        tenantConfiguration: state.tenantConfiguration,
        jwtToken: state.jwtToken,
    }));

    const handleToggleSelectMode = () => {
        setIsSelectMode((prev) => {
            if (prev) {
                setSelectedTokens(new Set());
            }
            return !prev;
        });
    };

    const handleToggleSelectItem = (token: string) => {
        setSelectedTokens((prev) => {
            const next = new Set(prev);

            if (next.has(token)) {
                next.delete(token);
            } else {
                next.add(token);
            }

            return next;
        });
    };

    const handleToggleSelectAll = () => {
        const visibleItems = (data?.Items ?? []).filter((item) => {
            const liveStatus = liveProgress[item.Token]?.status ?? item.Status;
            return activeStatuses.has(liveStatus);
        });
        const allSelected = visibleItems.every((item) => selectedTokens.has(item.Token));
        const next = new Set(selectedTokens);

        visibleItems.forEach((item) => {
            if (allSelected) {
                next.delete(item.Token);
            } else {
                next.add(item.Token);
            }
        });

        setSelectedTokens(next);
    };

    const handleOpenBatchReprocess = () => {
        let firstToken: string | undefined;
        selectedTokens.forEach((t) => {
            if (firstToken === undefined) {
                firstToken = t;
            }
        });
        const firstItem = data?.Items.find((item) => item.Token === firstToken);
        const iv: Record<string, string> = firstItem
            ? Object.fromEntries(firstItem.Params.map((p) => [p.Param, p.Value]))
            : {};
        setBatchReprocessParams(iv);
        setBatchReprocessInitialValues(iv);
        setBatchReprocessKey((k) => k + 1);
        setBatchReprocessOpen(true);
    };

    const handleBatchConfirmReprocess = () => {
        if (!validateReprocessParams(batchReprocessParams)) {
            setBatchValidationError(true);
            return;
        }

        setBatchValidationError(false);

        const paramsList: IQueueItemParam[] = Object.entries(batchReprocessParams).map(([key, value]) => ({
            Param: key,
            Value: String(value),
        }));

        const itemsList: SetupReprocessingItemParams[] = [];
        selectedTokens.forEach((token) => {
            itemsList.push({ Token: token, Params: paramsList });
        });

        batchSetupReprocessing(
            {
                Items: itemsList,
            },
            {
                onSuccess: () => {
                    setBatchReprocessOpen(false);
                    setBatchReprocessParams({});
                    setSelectedTokens(new Set());
                    setIsSelectMode(false);
                    queryClient.invalidateQueries(['QueueGroupById', groupToken]);
                },
            }
        );
    };

    useEffect(() => {
        if (isLoading || !data || data.Items.length === 0) {
            return;
        }

        const signalR = new SignalR(`${tenantConfiguration?.endpoints.signalR}/CustomGroupHub`, jwtToken);

        const joinGroups = (connection: SignalR) => {
            data.Items.forEach(item => {
                connection.joinGroup('qi-' + item.Token);
            });
        };

        joinGroups(signalR);

        signalR.onReconnected(() => joinGroups(signalR));

        signalR.listen('progressQueue', (progressData: QueueProgressData) => {
            console.log('progressDate', progressData);
            setLiveProgress(prev => ({ ...prev, [progressData.itemQueueToken]: progressData }));
        });

        return () => {
            signalR.stop();
        };
    }, [data]);

    useEffect(() => {
        if (!searchText.trim() || !data) {
            return;
        }

        const lower = searchText.toLowerCase();
        let bestMatch: IQueueGroupItem | null = null;
        let bestScore = -1;

        for (const item of data.Items) {
            const name = (item.Name || item.Token).toLowerCase();
            const idx = name.indexOf(lower);

            if (idx === -1) {
                continue;
            }

            const score = idx === 0 ? 1000 - lower.length : 500 - idx;

            if (score > bestScore) {
                bestScore = score;
                bestMatch = item;
            }
        }

        if (bestMatch) {
            const el = itemRefsMap.current[bestMatch.Token];

            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [searchText, data]);

    if (isLoading) {
        return (
            <div css={containerStyle}>
                <div className='detail-loading'>
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div css={containerStyle}>
                <div className='detail-back-row'>
                    <IconButton size='small' onClick={onBack} aria-label='Back to list'>
                        <ArrowBackIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                    </IconButton>
                    <Typography variant='body2' sx={{ color: '#9e9e9e' }}>
                        Group not found.
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div css={containerStyle}>
            <div className='detail-back-row'>
                <Tooltip title='Back to list' placement='right'>
                    <IconButton size='small' onClick={onBack} aria-label='Back to list'>
                        <ArrowBackIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                    </IconButton>
                </Tooltip>
                <Typography variant='body2' sx={{ color: '#9e9e9e' }}>
                    Batches
                </Typography>
                <Typography variant='body2' sx={{ color: '#9e9e9e', mx: '2px' }}>
                    /
                </Typography>
                <Typography variant='body2' sx={{ color: '#212121', fontWeight: 600 }}>
                    {data.Name || data.Token}
                </Typography>
            </div>

            <Paper elevation={0} className='detail-header-card'>
                <div className='detail-header-top'>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            backgroundColor: '#e3f2fd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <AccountTreeOutlinedIcon sx={{ color: '#1565c0', fontSize: 26 }} />
                    </Box>
                    <div className='detail-header-title-block'>
                        <Typography variant='h6' sx={{ fontWeight: 700, color: '#212121', lineHeight: 1.3 }}>
                            {data.Name || '—'}
                        </Typography>

                        <div className='detail-header-meta'>
                            <QueueTypeBadge queueType={data.QueueType} size='medium' />
                            <Box className='detail-meta-item'>
                                <PersonOutlineIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
                                <Typography variant='body2' sx={{ color: '#616161' }}>
                                    {data.Creator}
                                </Typography>
                            </Box>
                            <Box className='detail-meta-item'>
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                                <Typography variant='body2' sx={{ color: '#616161' }}>
                                    {formatShortDate(data.CreationDate)}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 0.25,
                                    borderRadius: '12px',
                                    backgroundColor: '#e3f2fd',
                                    color: '#1565c0',
                                }}
                            >
                                <Typography variant='caption' sx={{ fontWeight: 700, color: 'inherit' }}>
                                    {data.TotalItems} {data.TotalItems === 1 ? 'item' : 'items'}
                                </Typography>
                            </Box>
                        </div>
                    </div>
                    <Box sx={{ ml: 'auto', alignSelf: 'flex-start', flexShrink: 0, display: 'flex', gap: 1 }}>
                        <Button
                            variant='outlined'
                            disableElevation
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => setIsEditOpen(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            Edit
                        </Button>
                        <Button
                            variant='contained'
                            disableElevation
                            startIcon={<NoteAddOutlinedIcon />}
                            onClick={() => setIsAddFilesOpen(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            Add Files
                        </Button>
                    </Box>
                </div>

            </Paper>

            <div className='detail-items-section'>
                <div className='detail-items-title'>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '10px',
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #e0e0e0',
                            cursor: 'default',
                        }}
                    >
                        <Typography variant='caption' sx={{ fontWeight: 700, color: '#616161', lineHeight: 1 }}>
                            {data.TotalItems}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#9e9e9e', lineHeight: 1 }}>
                            Total
                        </Typography>
                    </Box>
                    {STATUS_DISPLAY_ORDER.map((s) => {
                        const count = data.StatusCounts[s] ?? 0;
                        const meta = STATUS_META[s];
                        const isActive = activeStatuses.has(s);

                        return (
                            <Tooltip
                                key={s}
                                title={`${meta.label}: ${count} of ${data.TotalItems}`}
                                placement='top'
                            >
                                <Box
                                    onClick={() => {
                                        setActiveStatuses((prev) => {
                                            const next = new Set(prev);

                                            if (next.has(s)) {
                                                next.delete(s);
                                            } else {
                                                next.add(s);
                                            }

                                            return next;
                                        });
                                    }}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        backgroundColor: isActive ? meta.bg : '#fafafa',
                                        border: isActive ? `1.5px solid ${meta.borderColor}` : '1px solid #e0e0e0',
                                        color: isActive ? meta.color : '#bdbdbd',
                                        transition: 'all 0.15s ease',
                                        userSelect: 'none',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: '50%',
                                            backgroundColor: isActive ? meta.color : '#bdbdbd',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography variant='caption' sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1 }}>
                                        {count}
                                    </Typography>
                                    <Typography variant='caption' sx={{ color: 'inherit', opacity: 0.75, lineHeight: 1 }}>
                                        {meta.label}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            size='small'
                            variant={isSelectMode ? 'contained' : 'outlined'}
                            disableElevation
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                            onClick={handleToggleSelectMode}
                            sx={{ textTransform: 'none', fontSize: 13 }}
                        >
                            Reprocessar Itens
                        </Button>
                        {isSelectMode && (
                            <Fragment>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    disableElevation
                                    onClick={handleToggleSelectAll}
                                    sx={{ textTransform: 'none', fontSize: 13 }}
                                >
                                    {(data?.Items ?? []).filter((item) => {
                                        const liveStatus = liveProgress[item.Token]?.status ?? item.Status;
                                        return activeStatuses.has(liveStatus);
                                    }).every((item) => selectedTokens.has(item.Token))
                                        ? 'Desmarcar Todos'
                                        : 'Marcar Todos'}
                                </Button>
                                <Button
                                    size='small'
                                    variant='contained'
                                    disableElevation
                                    color='warning'
                                    startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                                    disabled={selectedTokens.size === 0}
                                    onClick={handleOpenBatchReprocess}
                                    sx={{ textTransform: 'none', fontSize: 13 }}
                                >
                                    Reprocessar ({selectedTokens.size})
                                </Button>
                            </Fragment>
                        )}
                        <TextField
                            size='small'
                            placeholder='Search items...'
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: 240,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: 13,
                                },
                            }}
                        />
                    </Box>
                </div>

                {data.Items.length === 0 ? (
                    <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: '48px 24px', textAlign: 'center' }}>
                        <Typography variant='body2' sx={{ color: '#9e9e9e' }}>
                            No items in this group.
                        </Typography>
                    </Paper>
                ) : (
                    <div className='detail-items-list'>
                        {data.Items.filter((item) => {
                            const liveStatus = liveProgress[item.Token]?.status ?? item.Status;
                            return activeStatuses.has(liveStatus);
                        }).map((item) => (
                            <div key={item.Token} ref={setItemRef(item.Token)}>
                                <ItemCard
                                    item={item}
                                    liveProgress={liveProgress[item.Token]}
                                    projectToken={uploadInfo?.ProjectToken}
                                    queueType={data.QueueType}
                                    groupToken={groupToken}
                                    showCheckbox={isSelectMode}
                                    isSelected={selectedTokens.has(item.Token)}
                                    onToggleSelect={handleToggleSelectItem}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <QueueProcessingCreateGroupPopup
                open={isEditOpen}
                onChangeOpen={setIsEditOpen}
                editGroup={{ token: data.Token, name: data.Name, queueType: data.QueueType }}
                onSaved={() => undefined}
            />
            <QueueProcessingAddFilesWizard
                open={isAddFilesOpen}
                onClose={() => setIsAddFilesOpen(false)}
                groupToken={groupToken}
                queueType={data.QueueType}
            />
            <GeopostPopup
                open={batchReprocessOpen}
                onChangeOpen={setBatchReprocessOpen}
                title={`Reindex ${selectedTokens.size} ${selectedTokens.size === 1 ? 'item' : 'itens'}`}
                width={600}
                height={600}
                resizable={false}
                draggable
                overlay
                footer={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        {batchValidationError && (
                            <Typography variant='caption' sx={{ color: '#c62828', mr: 'auto' }}>
                                Preencha todos os campos obrigatórios.
                            </Typography>
                        )}
                        <Button
                            size='small'
                            variant='outlined'
                            startIcon={<ContentPasteIcon sx={{ fontSize: 14 }} />}
                            disabled={!getCopiedParamValues()}
                            onClick={() => {
                                const copied = getCopiedParamValues();

                                if (copied) {
                                    setBatchReprocessParams(copied);
                                    setBatchReprocessInitialValues(copied);
                                    setBatchReprocessKey((k) => k + 1);
                                }
                            }}
                            sx={{ textTransform: 'none', mr: 'auto' }}
                        >
                            Paste values
                        </Button>
                        <Button variant='outlined' onClick={() => setBatchReprocessOpen(false)} sx={{ textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button variant='contained' onClick={handleBatchConfirmReprocess} disabled={isBatchReprocessing} sx={{ textTransform: 'none' }}>
                            {isBatchReprocessing ? 'Processando...' : 'Confirmar'}
                        </Button>
                    </Box>
                }
            >
                <QueueProcessingParams
                    key={batchReprocessKey}
                    queueType={data.QueueType}
                    onParamsChange={setBatchReprocessParams}
                    initialValues={batchReprocessInitialValues}
                />
            </GeopostPopup>
        </div>
    );
}
